import { type LinkProps } from "next/link"
import { type ReactNode } from "react"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import Text from "@/app/(site)/_components/Atoms/Text"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"

interface ButtonLinkProps extends LinkProps {
    variant: "primary" | "secondary"
    color: "black" | "white"
    size: "large" | "medium" | "small"
    fullWidth?: boolean
    children: ReactNode
    href: string
    className?: string
}

const baseStyles = clsx(
    "inline-flex items-center justify-center gap-8 rounded-full font-trimRegularNew font-light uppercase",
    "transition duration-300 ease-in-out",
    FOCUS_VISIBLE_CLASS
)
const sizeStyles = {
    large: "md:px-[24px] md:py-[16px] md:text-lg px-16 py-14 text-16 !leading-[18px]",
    medium: "px-16 py-16 text-16 !leading-[15px]",
    small: "px-16 py-13 text-sm !leading-[15px]"
}
const colorStyles = {
    primary: {
        black: "bg-black text-white hover:bg-opacity-90",
        white: "bg-white text-black hover:bg-opacity-90"
    },
    secondary: {
        black: "border border-black text-black hover:border-opacity-90",
        white: "border border-white text-white hover:border-opacity-90"
    }
}
const fullWidthStyles = "w-full"

const ButtonLink = ({
    variant,
    color,
    size,
    fullWidth,
    children,
    href,
    className,
    ...linkProps
}: ButtonLinkProps) => {
    const textSize = size === "large" ? "medium" : "small"
    const buttonStyles = clsx(
        baseStyles,
        sizeStyles[size],
        colorStyles[variant][color],
        { [fullWidthStyles]: fullWidth },
        className
    )

    return (
        <Text size={textSize} weight="regular" asChild>
            <DynamicLink href={href} className={buttonStyles} {...linkProps}>
                {children}
            </DynamicLink>
        </Text>
    )
}

export default ButtonLink
